import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Film, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function SignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success("Account created! Please sign in.");
      navigate("/signin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#020B23] flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-xl bg-[#4D11F6] flex items-center justify-center">
              <Film className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Sora, sans-serif' }}>Multiplai</span>
          </div>
          <h1 className="text-white text-4xl font-bold leading-tight mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Start generating<br />property videos today
          </h1>
          <p className="text-[#94969C] text-lg leading-relaxed max-w-md">
            Create your account and begin turning listings into polished walkthrough videos in minutes.
          </p>
        </div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#4D11F6]/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F3F3F9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-9 h-9 rounded-xl bg-[#4D11F6] flex items-center justify-center">
              <Film className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-[#020B23] font-bold text-xl" style={{ fontFamily: 'Sora, sans-serif' }}>Multiplai</span>
          </div>

          <h2 className="text-3xl font-bold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>Create account</h2>
          <p className="text-[#676970] mb-8">Fill in your details to get started</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#1A2238] font-medium">Full Name</Label>
              <Input id="name" placeholder="Jane Doe" value={name} onChange={(e) => setName(e.target.value)}
                required data-testid="signup-name-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1A2238] font-medium">Email</Label>
              <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="signup-email-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1A2238] font-medium">Password</Label>
              <Input id="password" type="password" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="signup-password-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-[#1A2238] font-medium">Confirm Password</Label>
              <Input id="confirm" type="password" placeholder="Repeat your password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required data-testid="signup-confirm-input"
                className="h-11 bg-white border-[#E2E4E7] focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/20" />
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-submit-btn"
              className="w-full h-11 bg-[#4D11F6] hover:bg-[#3A0BC0] text-white font-medium rounded-lg transition-colors">
              {loading ? "Creating account..." : "Create account"}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#676970]">
            Already have an account?{" "}
            <Link to="/signin" className="text-[#4D11F6] font-medium hover:underline" data-testid="goto-signin-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
